import { Page, PageContentList } from 'shared/_components/page/styles';
import PageHeader from 'shared/_components/page/page-header';
import { Box, Button, Card, CardContent, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tab, Tabs } from '@mui/material';
import { useParams } from 'react-router';
import CurrentFileProvider, { useCurrentFile } from 'shared/Providers/CurrentFile';
import { useCallback, useContext } from 'react';
import { CardHeaderWithActions } from 'shared/_pages/account/styles';
import EditDocumentProvider, { useEditDocumentContext } from 'shared/hooks/use-edit-document';
import { isFinishFile } from 'shared/constants/pouchDB';
import { FILE_SCHEMA } from 'shared/constants/general';
import { FormikHelpers, useFormik } from 'formik';
import { TDocFile } from 'shared/constants/docs';
import useTabs from 'shared/components/Tabs/use-tabs';
import { useSharingContext } from 'shared/Providers/Sharing';
import useToggle from 'shared/hooks/use-toggle';
import { UserContext } from 'shared/Providers/User';
import TabPanel from 'shared/components/Tabs/TabPanel';
import YoutubeAnalytics from 'shared/components/YoutubeAnalytics/YoutubeAnalytics';
import { ANALYTICS_TYPES } from 'shared/utils/youtube';
import FileSeriesList from 'shared/Pages/common/FilePage/FinishVideo/Pages/FileSeriesList/FileSeriesList';
import { CardFieldsContainer } from 'shared/_ui/layout';
import FilePlayer from 'shared/_pages/video/file-player';
import FileCard from 'shared/_pages/video/file-card';
import FileMetadata from 'shared/_pages/video/file-metadata';
import FileNotes from 'shared/_pages/video/file-notes';
import FileDetails from 'shared/_pages/video/file-details';
import { fileDetailsSx, FilePageContentContainer } from 'shared/_pages/video/styles';
import FileImage from 'shared/_pages/video/file-image';
import ShareDialog from 'shared/_model/sharing/ShareDialog';
import { SharingType } from 'shared/_model/sharing/types'
import { IconMoreVert } from 'shared/_ui/icon'
import useMenu from 'shared/hooks/use-menu'
import DocumentShareIcon from 'shared/_model/sharing/document-share-icon'

function FilePageContent({ formik }: {
	// TODO: fix type. This brings an error on webapp build
	// formik: ReturnType<typeof useFormik<TDocFile>>;
	formik: ReturnType<typeof useFormik>;
}) {
	const { file, readFile } = useCurrentFile();
	const [tab, handleTabChange] = useTabs();
	const { sharingId } = useSharingContext();
	const { isEdit, toggleEdit } = useEditDocumentContext();
	const canShare = !sharingId;

	const [showShareSettings, toggleShowShareSettings] = useToggle();
	const { anchorEl, open, handleOpen, handleClose } = useMenu();

	const hasYoutube = !!file.youtube;

	const { permissions: { canUseApp } } = useContext(UserContext);

	const action = !canUseApp ? undefined : (
		<>
			{!isEdit && <Button onClick={() => toggleEdit(formik.resetForm)}>Edit</Button>}
			{isEdit && (
				<Button color="secondary" onClick={() => toggleEdit(formik.resetForm)}>
					Cancel
				</Button>
			)}
			{isEdit && (
				<Button onClick={formik.submitForm} disabled={formik.isSubmitting || !formik.dirty || !formik.isValid}>
					Save
				</Button>
			)}
			<IconButton onClick={handleOpen}>
				<IconMoreVert />
			</IconButton>
		</>
	);

	return (
		<Page sx={{ bgcolor: '#f8fafc' }}>
			<PageHeader breadcrumbs={['Videos']}>{file.title}</PageHeader>
			<PageContentList>
				<FilePageContentContainer>
					<FilePlayer {...file} />
					<FileDetails sx={fileDetailsSx} />
					<Card>
						<CardHeaderWithActions title={formik.values.title} action={action} />
						<CardContent>
							<Box display="flex" flexDirection="row" gap={4}>
								<FileImage formik={formik} isEdit={isEdit} />
								<FileCard formik={formik} />
							</Box>
							<Tabs value={tab} onChange={handleTabChange} sx={{ mt: 5, mb: 7 }}>
								<Tab label="Metadata" />
								{!sharingId && <Tab label="Notes" />}
								{!sharingId && <Tab label="Series" />}
								{!sharingId && hasYoutube && <Tab label="Analytics" />}
							</Tabs>
							<TabPanel value={tab} index={0}>
								<FileMetadata formik={formik} />
							</TabPanel>
							{!sharingId && (
								<>
									<TabPanel value={tab} index={1}>
										<FileNotes formik={formik} />
									</TabPanel>
									<TabPanel value={tab} index={2}>
										<CardFieldsContainer sx={{ minHeight: '15rem' }}>
											<FileSeriesList fileId={file._id} />
										</CardFieldsContainer>
									</TabPanel>
									{hasYoutube && (
										<TabPanel value={tab} index={3}>
											<CardFieldsContainer>
												<YoutubeAnalytics
													analyticType={ANALYTICS_TYPES.video}
													id={file.youtube && file.youtube.youtubeId}
												/>
											</CardFieldsContainer>
										</TabPanel>
									)}
								</>
							)}
						</CardContent>
					</Card>
				</FilePageContentContainer>
			</PageContentList>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<MenuItem disabled={isEdit} onClick={() => {
					handleClose();
					toggleShowShareSettings();
				}}>
					<ListItemIcon>
						<DocumentShareIcon sharing={file.sharing} type={SharingType.FILE} />
					</ListItemIcon>
					<ListItemText>Share</ListItemText>
				</MenuItem>
			</Menu>
			{canShare && showShareSettings && (
				<ShareDialog
					documentId={file._id}
					type={SharingType.FILE}
					handleClose={toggleShowShareSettings}
					onChange={readFile}
				/>
			)}
		</Page>
	);
}

function FilePageForm({file, onSubmit }: {
	file: TDocFile,
	onSubmit: (values: TDocFile, formikHelpers: FormikHelpers<TDocFile>) => Promise<unknown>
}) {
	const formik = useFormik<TDocFile>({
		initialValues: file,
		validationSchema: FILE_SCHEMA,
		enableReinitialize: true,
		onSubmit,
	});

	// TODO: fix type. This brings an error on webapp build
	return <FilePageContent formik={formik as any} />;
}

function FilePageInner() {
	const { file, saveFile } = useCurrentFile();
	const { resetEdit } = useEditDocumentContext();

	const handleSubmit = useCallback(
		async (values: Partial<typeof file>) => {
			await saveFile?.(values);
			resetEdit();
		},
		[saveFile, resetEdit]
	);

	if (!file._id) {
		return null;
	}

	if (!isFinishFile(file.type)) {
		return null;
	}

	return <FilePageForm file={file} onSubmit={handleSubmit} />;
}

export default function FilePage() {
	const { fileId } = useParams<{ fileId: string }>();

	return (
		<CurrentFileProvider fileId={fileId}>
			<EditDocumentProvider>
				<FilePageInner />
			</EditDocumentProvider>
		</CurrentFileProvider>
	);
}
